import {DateTime} from 'luxon';
import {createSelector} from 'reselect';

import {maxNumberOfFeatureType} from '../../../helpers/models/billingHelpers';
import {AppParametricSelector} from '../../../helpers/reselectHelpers';
import {
  CompanyFeaturesEnum,
  CompanyStatusesEnum,
  CompanyTrialPlansEnum,
  isCompanyTrialPlan,
  pastDueInvoiceString,
  recentChargeFailedString,
} from '../../../models/api/companyModel';
import {AppState} from '../../appState';
import {getHasNotificationCenter} from '../../featureFlags/featureFlagsSelectors';
import {getHost} from '../dataSelectors';
import {getFlatActiveOrPendingTeammates, getIsUserVisitor} from '../teammates/teammatesSelectors';

/** Front's own company ID. */
const frontCompanyId = 11;

export function getCompany(state: AppState) {
  return state.data.company;
}

export function getCompanyId(state: AppState) {
  return state.data.company.id;
}

export function getCompanyUrl(state: AppState) {
  return state.data.company.url;
}

export function getCompanyName(state: AppState) {
  return state.data.company.name;
}

export function getCompanyCreatedAt(state: AppState) {
  return state.data.company.createdAt;
}

export function getCompanyRegion(state: AppState) {
  return state.data.company.region;
}

export function getCompanyPreferences(state: AppState) {
  return state.data.company.preferences;
}

export function getIsInvitingNewVisitorsEnabled(state: AppState) {
  return state.data.company.preferences.isInvitingNewVisitorsEnabled;
}

export function getHasAutocompleteVisitorContacts(state: AppState) {
  return state.data.company.preferences.hasAutocompleteVisitorContacts;
}

export function getIsUserAllowedToEnableIntegrations(state: AppState) {
  return state.data.company.preferences.isUserAllowedToEnableIntegrations;
}

export const getCompanyHost = createSelector([getHost, getCompany], (host, company) => {
  // If the company doesn't have SSO. Use the boot-provided host.
  if (!company.subdomain || !company.preferences.hasSso) {
    return host;
  }

  // Otherwise, build the host based on the company subdomain.
  return `https://${company.subdomain}.frontapp.com`;
});

export const getCompanySubdomain = createSelector(getCompany, (company) => company.subdomain);

export function getIsNotificationCenterEnabled(state: AppState) {
  const {hasNotificationCenter} = state.data.company.preferences;
  return hasNotificationCenter || getHasNotificationCenter(state);
}

export function getSendBumpStrategy(state: AppState) {
  return state.data.company.preferences.sendBumpStrategy;
}

export function getSessionIdleTimeout(state: AppState) {
  return state.data.company.preferences.sessionIdleTimeout;
}

export const getHasInternalDiscussions = createSelector(
  getCompany,
  (company) => company.preferences.hasInternalDiscussions,
);

export const getCanMoveToInaccessibleInbox = createSelector(
  getCompany,
  (company) => company.preferences.canMoveToInaccessibleInbox,
);

export const getIsInTrial = createSelector(getCompany, (company) => company.isInTrial);

export const getIsInManagedTrial = createSelector(getCompany, (company) => company.isInManagedTrial);

export const getIsInTrialOrManagedTrial = createSelector(
  getCompany,
  (company) => company.isInTrial || company.isInManagedTrial,
);

export const getIsCustomer = createSelector(
  getCompany,
  // Company is a customer if they are not on a trial plan.
  (company) => Boolean(company.plan && !isCompanyTrialPlan(company.plan)),
);

export const getIsFreePlan = createSelector(
  getCompany,
  (company) => company.plan === CompanyTrialPlansEnum.FREE,
);

/**
 * If a company is on the FREE plan without a Stripe ID, then they were moved
 * off TRIAL to FREE after their trial expired by the trial cron job.
 *
 * Otherwise, if they are on the FREE plan and have a Stripe ID, then they are a
 * churn, since churning also moves the company to FREE.
 */
export const getHasTrialExpired = createSelector(
  getCompany,
  (company) => company.plan === CompanyTrialPlansEnum.FREE && !company.hasStripeId,
);

export const getDaysLeftInTrial = createSelector(getCompany, (company) => {
  if (!company.trialEndDate) {
    return Infinity;
  }

  const nowDateTime = DateTime.fromMillis(Date.now());
  const trialEndDateTime = DateTime.fromMillis(company.trialEndDate);
  // Round it up, so 1.5 days becomes 2 days remaining.
  const daysRemaining = Math.ceil(trialEndDateTime.diff(nowDateTime, 'days').days);

  if (daysRemaining < 0) {
    return 0;
  }

  return daysRemaining;
});

export const getShouldDisplayCustomPricingAlert = createSelector(getCompany, (company) => {
  // Companies that are managed or have alerts disabled should NOT get custom pricing alerts
  const isManaged = company.preferences.customerSuccessManagerEmail;
  const isDisabled = company.preferences.isCustomPricingAlertsDisabled;
  return !(isManaged || isDisabled);
});

export const getDaysLeftInDiscount = createSelector(getCompany, (company) => {
  if (!company.discountEndDate) {
    return undefined;
  }

  const nowDateTime = DateTime.fromMillis(Date.now());
  const discountEndDateTime = DateTime.fromMillis(company.discountEndDate);
  // Round it up, so 1.5 days becomes 2 days remaining.
  const daysRemaining = Math.ceil(discountEndDateTime.diff(nowDateTime, 'days').days);

  if (daysRemaining < 0) {
    return 0;
  }

  return daysRemaining;
});

export const getRecentChargeFailed = createSelector(
  getCompany,
  (company) => company.stripeError && company.stripeError.includes(recentChargeFailedString),
);

export const getIsBilledThroughOtherCompany = createSelector(
  getCompany,
  (company) => company.isBilledThroughOtherCompany,
);

export const getHasInvoicesPastDue = createSelector(
  getCompany,
  (company) => company.stripeError && company.stripeError.includes(pastDueInvoiceString),
);

export const getIsBlockedBilling = createSelector(
  getCompany,
  (company) => company.status === CompanyStatusesEnum.BLOCKED_BILLING,
);
export const getIsBlocked = createSelector(
  getCompany,
  (company) => company.status !== CompanyStatusesEnum.OK,
);

export const getCanInviteVisitors = createSelector(
  [getIsUserVisitor, getIsInvitingNewVisitorsEnabled, getIsInTrial],
  (isUserVisitor, hasInviteVisitors, isInTrial) => hasInviteVisitors && !isInTrial && !isUserVisitor,
);

export const getCanAutocompleteMentionContacts = createSelector(
  [getCanInviteVisitors, getHasAutocompleteVisitorContacts],
  (canInviteVisitors, hasAutocompleteMentionContacts) => canInviteVisitors && hasAutocompleteMentionContacts,
);

export const getCanEditComments = createSelector(
  [getCompany],
  (company) => company.preferences.canEditComments,
);

export function getShouldReplyWithOutlookStyle(state: AppState) {
  return state.data.company.preferences.shouldReplyWithOutlookStyle;
}

export const getFindHasCompanyFeature = createSelector(
  getCompany,
  (company) => (feature: CompanyFeaturesEnum) =>
    company.features.some((f: string) => f === feature || f.startsWith(`${feature}:`)),
);

export const getCompanyPlan = createSelector(getCompany, (company) => company.plan);

export const getHasSupportChatFeature = createSelector(getFindHasCompanyFeature, (hasCompanyFeature) =>
  hasCompanyFeature(CompanyFeaturesEnum.SUPPORT_CHAT),
);

export const getCompanyHasSlaRulesFeature = createSelector(getFindHasCompanyFeature, (hasCompanyFeature) =>
  hasCompanyFeature(CompanyFeaturesEnum.SLA_RULES),
);

export const getCompanyHasAnalytics = createSelector(getFindHasCompanyFeature, (hasCompanyFeature) =>
  hasCompanyFeature(CompanyFeaturesEnum.ANALYTICS),
);

export const getCompanyHasAdvancedAnalytics = createSelector(getFindHasCompanyFeature, (hasCompanyFeature) =>
  hasCompanyFeature(CompanyFeaturesEnum.ADVANCED_ANALYTICS),
);

export const getCompanyHasAccountsAnalytics = createSelector(getFindHasCompanyFeature, (hasCompanyFeature) =>
  hasCompanyFeature(CompanyFeaturesEnum.ACCOUNT_ANALYTICS),
);

export const getCompanyHasCsatSurveysFeature = createSelector(getFindHasCompanyFeature, (hasCompanyFeature) =>
  hasCompanyFeature(CompanyFeaturesEnum.SURVEYS),
);

export const getCompanyHasCrmSyncFeature = createSelector(getFindHasCompanyFeature, (hasCompanyFeature) =>
  hasCompanyFeature(CompanyFeaturesEnum.CRM_SYNC),
);

export const getHasRuleExpressionsFeature = createSelector(getFindHasCompanyFeature, (hasCompanyFeature) =>
  hasCompanyFeature(CompanyFeaturesEnum.SMART_RULES),
);

export const getCompanyHasHiddenBilling = createSelector(
  getCompany,
  (company) => company.isBilledThroughOtherCompany || company.preferences.isBillingHidden,
);

export function getDefaultTeammateAccessRole(state: AppState) {
  return state.data.company.preferences.defaultTeammateAccessRole;
}

export function getCompanySize(state: AppState) {
  return state.data.company.preferences.companySize;
}

/** Whether the current company is Front's own Front instance. */
export const getIsCompanyFront = createSelector(getCompany, (company) => company.id === frontCompanyId);

export const getCustomerSuccessManagerEmail = createSelector(
  getCompany,
  (company) => company.preferences.customerSuccessManagerEmail,
);

export const getCompanyContactsScope = createSelector(
  getCompany,
  (company) => company.preferences.companyContactsScope,
);

export const getCompanyPainPoints = createSelector(getCompany, (company) => company.preferences.painPoints);

// export const getCompanyMaxTeammates =
export function makeGetCompanyMaxTeammates(getIsUpgrading: AppParametricSelector<any, boolean>) {
  return createSelector([getCompany, getIsInTrial, getIsUpgrading], (company, isInTrial, isUpgrading) =>
    !isUpgrading && isInTrial
      ? company.preferences.maxTeammatesDuringTrial
      : maxNumberOfFeatureType('teammates', company.features, 2),
  );
}

export function makeGetCanCompanyAddTeammates(
  getNumberOfTeammatesToAdd: AppParametricSelector<any, number>,
  getCompanyMaxTeammates: ReturnType<typeof makeGetCompanyMaxTeammates>,
) {
  return createSelector(
    [getFlatActiveOrPendingTeammates, getNumberOfTeammatesToAdd, getCompanyMaxTeammates],
    (flatTeammates, numberOfTeammatestoAdd, maxTeammates) => {
      const numberOfTeammates = flatTeammates.length;

      return numberOfTeammates + numberOfTeammatestoAdd <= maxTeammates;
    },
  );
}

export const getCompanyHasGlobalRules = createSelector(
  [getFindHasCompanyFeature, getCompany],
  (hasCompanyFeature, company) =>
    hasCompanyFeature(CompanyFeaturesEnum.GLOBAL_RULES) ||
    maxNumberOfFeatureType('global_rules', company.features) > 0,
);
